import { PlatformLocation } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, OnInit, OnDestroy } from '@angular/core';
import { Router, RouterOutlet } from '@angular/router';
import {
  MSAL_GUARD_CONFIG,
  MsalBroadcastService,
  MsalGuardConfiguration,
  MsalService,
} from '@azure/msal-angular';
import {
  AccountInfo,
  AuthenticationResult,
  EventMessage,
  EventType,
  InteractionStatus,
} from '@azure/msal-browser';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { ToastModule } from 'primeng/toast';
import { Subject, filter, finalize, switchMap, takeUntil } from 'rxjs';
import { environment } from '../environments/environment';
import {
  applyPendingSetPasswordAuthority,
  clearSetPasswordFlow,
  getMalformedFirmloginRecoveryPath,
  getPostLogoutRedirectUri,
  getSetPasswordToken,
  hasPendingB2cRedirect,
  hasUnconsumedB2cHashResponse,
  hasPendingMsalInteraction,
  isFreshLoginCallback,
  isRoleSwitchInProgress,
  isSetPasswordFlow,
  isSetPasswordRoute,
  markSetPasswordAttempted,
  consumeFreshLoginCallback,
  markFreshLoginCallback,
  markRedirectHandleDone,
  clearRedirectHandleDone,
  relocatePendingB2cRedirect,
  shouldCompleteSetPasswordFlow,
  markSilentB2cRecovery,
} from './auth.config';
import { AdvisorDashboardLayoutService } from './pages/advisor-dashboard-layout/advisor-dashboard-layout.service';
import { AccountsControllerService, ClientTermsAcknowledgmentService } from './services/generated/services';
import { RoleService } from './services/role.service';
import { SharedModule } from './shared/shared.module';
import { AuthService } from './services/auth.service';
import { ReSubmissionControllerService } from './services/generated/services';
import { LoaderService } from './services/loader-service/loader.service';
import { CommonService } from './services/common.service';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { ChangeRolesDialogComponent } from './components/reusable-dialogs/change-roles-dialog/change-roles-dialog.component';
import { CreditCardUiIntegrationService } from './services/credit-card-ui-integration.service';
import { PublicControllerService } from './services/generated/services/public-controller.service';
import { getEmailFromAccount } from './utils/auth-utils';
import { VersionCheckService } from './services/version-check.service';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [RouterOutlet, ToastModule, DialogModule, ButtonModule, SharedModule],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
  providers: [MessageService],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AppComponent implements OnInit, OnDestroy {
  loginDisplay = false;
  isIframe = false;
  loginStatus: boolean = false;
  private readonly _destroying$ = new Subject<void>();
  showCustomerWarning: boolean = false;
  clientChangeAgreementsDetails: any = {};
  clientChangeAgreementsList: any = [];
  resubmitApplications: any = [];
  clientName : string = ''
  showResubmitApplications: boolean = false;
  logoutToastShown : boolean = false;
  showChangeRolesDialoge: DynamicDialogRef | null = null;
  showPersonalDetailsPopup : boolean = false;
  isCodeGetting: boolean = false;
  private redirectHandlingComplete = false;
  private userDetailsRequestKey: string | null = null;
  private jwtRestoreInProgress = false;
  constructor(
    @Inject(MSAL_GUARD_CONFIG) private readonly msalGuardConfig: MsalGuardConfiguration,
    private readonly authService: MsalService,
    private readonly msalBroadcastService: MsalBroadcastService,
    private readonly router: Router,
    private readonly broadcastService: MsalBroadcastService,
    private readonly msalService: MsalService,
    private readonly AccountsControllerService: AccountsControllerService,
    private readonly publicController: PublicControllerService,
    private readonly pLocation: PlatformLocation,
    private readonly roleService: RoleService,
    private readonly MessageService: MessageService,
    private readonly ClientTermsAcknowledgmentService: ClientTermsAcknowledgmentService,
    private readonly AdvisorDashboardLayoutService: AdvisorDashboardLayoutService,
    private readonly ReSubmissionControllerService: ReSubmissionControllerService,
    private readonly LoaderService: LoaderService,
    private readonly CommonService: CommonService,
    private readonly dialogService: DialogService,
    private readonly CreditCardUiIntegrationService: CreditCardUiIntegrationService,
    private readonly versionCheck: VersionCheckService,
    private readonly cdr: ChangeDetectorRef
  ) {
    this.versionCheck.initVersionCheck();

    this.broadcastService.msalSubject$
      .pipe(
        filter(
          (msg: EventMessage) => msg.eventType === EventType.ACTIVE_ACCOUNT_CHANGED
        ),
        takeUntil(this._destroying$)
      )
      .subscribe(() => {
        if (!this.redirectHandlingComplete) {
          return;
        }
        if (this.msalService.instance.getAllAccounts().length !== 0) {
          this.setLoginDisplay();
          this.cdr.markForCheck();
        }
      });

    this.broadcastService.inProgress$
      .pipe(
        filter(
          (status: InteractionStatus) => status === InteractionStatus.None
        ),
        filter(() => this.redirectHandlingComplete),
        takeUntil(this._destroying$)
      )
      .subscribe(() => {
        this.setLoginDisplay();
        this.cdr.markForCheck();
      });
  }

  ngOnInit() {
    this.isIframe = window !== window.parent && !window.opener;
    clearRedirectHandleDone();

    if (relocatePendingB2cRedirect(environment.mslConfig.redirectUri)) {
      return;
    }

    applyPendingSetPasswordAuthority();

    // Last-resort backstop only. Orphaned callbacks are now caught immediately in the
    // handleRedirectObservable next/error handlers below; this covers the narrower case
    // where that observable never settles at all (e.g. a stalled authority-metadata fetch),
    // leaving no handler to fire. Expected to be a no-op in normal flows.
    if (hasPendingB2cRedirect() && !isSetPasswordFlow()) {
      const safetyTimer = setTimeout(() => {
        if (hasPendingB2cRedirect() && !isSetPasswordFlow()) {
          console.warn('B2C callback hash still present after 6s — clearing and redirecting to login.');
          sessionStorage.clear();
          this.msalService.instance.clearCache();
          this.roleService.clearAllCookies();
          this.redirectToFirmLogin(true);
        }
      }, 6000);
      // Cancel the timer if the component is destroyed (navigated away) first.
      this._destroying$.subscribe(() => clearTimeout(safetyTimer));
    }

    const recoveryPath = getMalformedFirmloginRecoveryPath(window.location.pathname);
    if (recoveryPath) {
      window.location.replace(recoveryPath);
      return;
    }

    this.authService.initialize().pipe(
      switchMap(() => this.authService.handleRedirectObservable()),
      finalize(() => {
        this.redirectHandlingComplete = true;
        markRedirectHandleDone();
        this.cdr.markForCheck();
      }),
      takeUntil(this._destroying$)
    ).subscribe({
      next: (result: AuthenticationResult | null) => {
        if (shouldCompleteSetPasswordFlow()) {
          const resetToken = getSetPasswordToken();
          clearSetPasswordFlow();
          if (resetToken) {
            markSetPasswordAttempted(resetToken);
          }
          this.finishPasswordResetAndGoToLogin();
          return;
        }
        if (isSetPasswordFlow()) {
          clearSetPasswordFlow();
        }
        if (result?.account) {
          markFreshLoginCallback();
          this.authService.instance.setActiveAccount(result.account);
          this.loginStatus = true;
          this.loginDisplay = true;
          this.roleService.clearAllCookies();
          this.completeLoginAfterRedirect(result);
          this.cdr.markForCheck();
        } else if (hasUnconsumedB2cHashResponse()) {
          // MSAL settled without an account but its fragment response is still in the URL:
          // an orphaned callback, e.g. a bookmarked authorize URL replayed outside the
          // loginRedirect() that minted its state/PKCE. Recover now instead of leaving the
          // user on a blank page until the safety timer below notices.
          console.warn('Unmatched B2C callback — recovering to firm login.');
          this.finishPasswordResetAndGoToLogin(true);
        } else {
          this.setLoginDisplay();
          this.cdr.markForCheck();
        }
      },
      error: (error) => {
        console.error('B2C redirect error:', error);
        const msg: string = error?.errorMessage ?? error?.message ?? String(error);
        const setPasswordToken = getSetPasswordToken();
        clearSetPasswordFlow();
        if (setPasswordToken) {
          markSetPasswordAttempted(setPasswordToken);
        }
        if (msg.includes('AADB2C90091')) {
          this.finishPasswordResetAndGoToLogin();
          return;
        }
        console.warn('Clearing failed B2C callback and redirecting to login.');
        sessionStorage.clear();
        this.msalService.instance.clearCache();
        this.roleService.clearAllCookies();
        this.finishPasswordResetAndGoToLogin(true);
      }
    });

    this.msalBroadcastService.msalSubject$
      .pipe(
        filter(
          (msg: EventMessage) => msg.eventType === EventType.LOGOUT_SUCCESS
        ),
        takeUntil(this._destroying$)
      )
      .subscribe((result: EventMessage) => {
        this.userDetailsRequestKey = null;
        this.setLoginDisplay();
        this.checkAndSetActiveAccount();
        this.cdr.markForCheck();
      });

    this.msalBroadcastService.msalSubject$
      .pipe(
        filter(
          (msg: EventMessage) => msg.eventType === EventType.LOGIN_SUCCESS
        ),
        takeUntil(this._destroying$)
      )
      .subscribe((result: EventMessage) => {
        const payload = result.payload as AuthenticationResult;
        markFreshLoginCallback();
        this.authService.instance.setActiveAccount(payload.account);
        this.loginStatus = true;
        this.loginDisplay = true;
        this.cdr.markForCheck();

        if (payload.accessToken != null) {
          sessionStorage.setItem('JwtToken', payload.accessToken);
        } else if (payload.account) {
          this.msalService.acquireTokenSilent({
            account: payload.account,
            scopes: ['https://'+environment.mslConfig.tenantName+'.onmicrosoft.com/'+environment.mslConfig.clientId+'/read']
          }).subscribe({
            next: (result: AuthenticationResult) => {
              sessionStorage.setItem('JwtToken', result.accessToken);
            },
            error: (err) => {
              console.error('Token acquisition failed', err);
            }
          });
        }
      });

      this.LoaderService.toast$.subscribe(() => {
          this.MessageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Your session has expired due to inactivity. Please log in again to continue.',
            life: 3000
          });
      });

      this.LoaderService.sblErrorToast$.subscribe(({ referenceId }) => {
          const detail = referenceId
            ? `Something went wrong. Please try again or contact support. Reference ID: ${referenceId}`
            : 'Something went wrong. Please try again or contact support.';
          this.MessageService.add({
            severity: 'error',
            summary: 'Error',
            detail,
            life: 6000
          });
      });

  }

  setLoginDisplay() {
    if (hasPendingMsalInteraction()) {
      return;
    }
    if (isRoleSwitchInProgress()) {
      return;
    }

    const accounts = this.msalService.instance.getAllAccounts();
    const activeAccount = this.msalService.instance.getActiveAccount();
    this.loginStatus = accounts.length > 0 || !!activeAccount;
    this.loginDisplay = this.loginStatus;

    if (this.loginStatus) {
      this.handleAuthenticatedLoginDisplay(accounts, activeAccount);
    } else {
      this.handleUnauthenticatedLoginDisplay();
    }
  }

  private handleAuthenticatedLoginDisplay(accounts: any[], activeAccount: any): void {
    if (!sessionStorage.getItem('JwtToken')) {
      if (isFreshLoginCallback()) {
        return;
      }
      const profile_data =
        this.msalService.instance.getActiveAccount() ?? accounts[0];
      if (profile_data) {
        this.tryRestoreJwtToken(profile_data);
        return;
      }
      if (this.hasStaleAppSession()) {
        this.performCleanLogout();
        return;
      }
    }

    this.consumeAuthCodeFromUrl('?code=');

    const profile_data = activeAccount ?? accounts[0];
    this.ensureActiveAccount(profile_data);
    this.getUserDetailsIfNeeded(profile_data, 'N');
  }

  private handleUnauthenticatedLoginDisplay(): void {
    this.consumeAuthCodeFromUrl('/?code=');
    if (
      this.pLocation.href.includes('customLendingDocZipDownload') ||
      this.pLocation.href.includes('txnApproval') ||
      this.pLocation.href.includes('sblApproval') ||
      this.pLocation.href.includes('firmlogin') ||
      this.pLocation.href.includes('setpassword') ||
      this.pLocation.href.includes('advyzon-callback') ||
      this.pLocation.pathname.includes('/auth/login')
    ) {
      if (this.pLocation.pathname.includes('/auth/login')) {
        this.router.navigate(['/firmlogin/fispoke']);
      }
      return;
    }
    if (this.isProtectedAppRoute()) {
      return;
    }
    // Not authenticated and not on a known entry/public route.
    this.redirectToFirmLogin();
  }

  /** Kick off credit-card token exchange when the URL carries an auth `?code=` and we are not already fetching. */
  private consumeAuthCodeFromUrl(marker: string): void {
    if (this.pLocation.href.includes(marker) && !this.isCodeGetting) {
      this.isCodeGetting = true;
      const code:any = this.pLocation.href?.split('?code=')[1];
      console.log('code', code);
      this.CreditCardUiIntegrationService.getCreditCardAccessTokenWithCode(code);
    }
  }

  private isProtectedAppRoute(): boolean {
    const path = this.pLocation.pathname;
    return path.includes('advisor-dashboard') || path.includes('admin-dashboard');
  }

  /**
   * @param silent When true, this redirect follows an unusable B2C callback (bookmarked
   * authorize URL, expired state, etc.) rather than a plain unauthenticated visit — mark it
   * so firm-login skips the forced re-prompt and lets the still-warm B2C session complete quietly.
   */
  private redirectToFirmLogin(silent = false): void {
    const firmname = localStorage.getItem('firmname') || 'fispoke';
    const roleType = localStorage.getItem('roleType') || '';
    const path = roleType === 'adv'
      ? `/firmlogin/${firmname}/adv`
      : `/firmlogin/${firmname}`;
    if (silent) {
      markSilentB2cRecovery();
    }
    this.router.navigate([path]);
  }

  /**
   * Password reset finished — clear MSAL and send user to firm login with new password.
   * @param silent See redirectToFirmLogin — only pass true from the generic B2C-callback-error
   * branch, never from the genuine set-password / AADB2C90091 branches above it.
   */
  private finishPasswordResetAndGoToLogin(silent = false): void {
    const firmname = localStorage.getItem('firmname') || '';
    const roleType = localStorage.getItem('roleType') || '';
    const logo = localStorage.getItem('LOGO');
    const firmId = localStorage.getItem('FIRMID');
    const ssoButton = localStorage.getItem('sso_button_name');
    const attemptedToken = sessionStorage.getItem('setPasswordAttempted');
    sessionStorage.clear();
    if (attemptedToken) {
      sessionStorage.setItem('setPasswordAttempted', attemptedToken);
    }
    this.msalService.instance.clearCache();
    this.roleService.clearAllCookies();
    localStorage.clear();
    if (firmname) {
      localStorage.setItem('firmname', firmname);
    }
    if (roleType) {
      localStorage.setItem('roleType', roleType);
    }
    if (logo) {
      localStorage.setItem('LOGO', logo);
    }
    if (firmId) {
      localStorage.setItem('FIRMID', firmId);
    }
    if (ssoButton) {
      localStorage.setItem('sso_button_name', ssoButton);
    }
    this.loginStatus = false;
    this.loginDisplay = false;
    this.userDetailsRequestKey = null;
    this.redirectToFirmLogin(silent);
  }

  private tryRestoreJwtToken(account: AccountInfo): void {
    if (this.jwtRestoreInProgress || isRoleSwitchInProgress()) {
      return;
    }
    this.jwtRestoreInProgress = true;
    this.msalService.acquireTokenSilent({
      account,
      scopes: [
        'https://' +
          environment.mslConfig.tenantName +
          '.onmicrosoft.com/' +
          environment.mslConfig.clientId +
          '/read',
      ],
    }).subscribe({
      next: (result: AuthenticationResult) => {
        sessionStorage.setItem('JwtToken', result.accessToken);
        this.jwtRestoreInProgress = false;
        this.ensureActiveAccount(account);
        this.getUserDetailsIfNeeded(account, 'N');
        this.cdr.markForCheck();
      },
      error: (err) => {
        this.jwtRestoreInProgress = false;
        console.error('Silent token restore failed', err);
        if (this.hasStaleAppSession()) {
          this.performCleanLogout();
        }
        this.cdr.markForCheck();
      },
    });
  }

  private completeLoginAfterRedirect(result: AuthenticationResult): void {
    const account = result.account;
    if (!account) {
      return;
    }
    if (result.accessToken) {
      sessionStorage.setItem('JwtToken', result.accessToken);
      consumeFreshLoginCallback();
      this.getUserDetails(account, 'Y');
      return;
    }
    this.msalService.acquireTokenSilent({
      account,
      scopes: [
        'https://' +
          environment.mslConfig.tenantName +
          '.onmicrosoft.com/' +
          environment.mslConfig.clientId +
          '/read',
      ],
    }).subscribe({
      next: (tokenResult: AuthenticationResult) => {
        sessionStorage.setItem('JwtToken', tokenResult.accessToken);
        consumeFreshLoginCallback();
        this.getUserDetails(account, 'Y');
        this.cdr.markForCheck();
      },
      error: (err) => {
        console.error('Token acquisition failed after redirect', err);
        if (sessionStorage.getItem('JwtToken')) {
          consumeFreshLoginCallback();
          this.getUserDetails(account, 'Y');
        }
        this.cdr.markForCheck();
      },
    });
  }

  private hasStaleAppSession(): boolean {
    if (sessionStorage.getItem('JwtToken')) {
      return false;
    }
    if (isRoleSwitchInProgress()) {
      return false;
    }
    if (!this.redirectHandlingComplete) {
      return false;
    }
    if (hasPendingMsalInteraction()) {
      return false;
    }
    if (this.pLocation.pathname.includes('firmlogin')) {
      return false;
    }
    if (isSetPasswordRoute(this.pLocation.pathname)) {
      return false;
    }
    if (this.isProtectedAppRoute()) {
      return false;
    }
    // Public, no-auth pages (approval links, doc-zip download, advyzon callback). An advisor who is
    // still logged into the dashboard in the same browser has profile/roles in localStorage — without
    // this guard those would be read as a "stale session" and the advisor would be logged out and
    // bounced to login the moment they open an sblApproval/txnApproval link.
    if (
      this.pLocation.href.includes('sblApproval') ||
      this.pLocation.href.includes('txnApproval') ||
      this.pLocation.href.includes('customLendingDocZipDownload') ||
      this.pLocation.href.includes('advyzon-callback')
    ) {
      return false;
    }
    // MSAL accounts alone are normal right after MFA (token not stored yet).
    // Profile/roles on dashboard routes are an active session, not stale.
    return (
      !!localStorage.getItem('profile') || !!localStorage.getItem('roles')
    );
  }

  private performCleanLogout(): void {
    if (isRoleSwitchInProgress()) {
      return;
    }
    const firmname = localStorage.getItem('firmname') || '';
    const roleType = localStorage.getItem('roleType') || '';
    sessionStorage.clear();
    this.msalService.instance.clearCache();
    this.roleService.clearAllCookies();
    localStorage.clear();
    if (firmname) {
      localStorage.setItem('firmname', firmname);
    }
    if (roleType) {
      localStorage.setItem('roleType', roleType);
    }
    this.authService.logoutRedirect({
      postLogoutRedirectUri: getPostLogoutRedirectUri(),
    });
  }

  private ensureActiveAccount(account: AccountInfo): void {
    const activeAccount = this.msalService.instance.getActiveAccount();
    if (activeAccount?.homeAccountId === account.homeAccountId) {
      return;
    }
    this.msalService.instance.setActiveAccount(account);
  }

  private getUserDetailsIfNeeded(profile_data: AccountInfo, loginType: string): void {
    const requestKey = `${profile_data?.homeAccountId ?? profile_data?.localAccountId}:${loginType}`;
    if (this.userDetailsRequestKey === requestKey) {
      return;
    }
    this.userDetailsRequestKey = requestKey;
    this.getUserDetails(profile_data, loginType);
  }

  getUserDetails(profile_data: any, loginType: string) {
    if (isRoleSwitchInProgress()) {
      return;
    }
    if (isSetPasswordRoute() && !sessionStorage.getItem('JwtToken')) {
      return;
    }
    const email = getEmailFromAccount(profile_data);
    let azureId = profile_data?.localAccountId;
    if (email) {

      this.publicController.validateUpdateMsAzureUserId({ body: { email_id: email, ms_azure_id: azureId } }).subscribe({
        next: (response: any) => {
          if (response.status === 'SUCCESS' && response?.azure_user_id) {
            azureId = response?.azure_user_id;
            console.log('profile_data?.localAccountId ----> ', profile_data?.localAccountId);
            this.doFindRouting(azureId,profile_data, loginType);
          }else{
            this.MessageService.add({
              severity: 'error',
              summary: 'Error',
              detail: 'We couldn’t verify your account. Please sign out and sign in again. If this continues, contact your administrator',
              life: 5000
            });
            this.authService.logoutRedirect();
          }

        }, error: (err: any) => {
          this.userDetailsRequestKey = null;
          console.error('Error validating Azure user ID', err);
        }
      });
    }else{
      this.doFindRouting(azureId,profile_data, loginType);
    }
  }
  doFindRouting(azureId: any, profile_data: any, loginType: string) {
    if (this.CommonService.isMobileDevice()) {
      this.showMobileNotSupportedMessage();
      this.authService.logoutRedirect();
      return;
    }
    this.AccountsControllerService.getUserDetailsByAzureuserid({ body: { azure_userid: azureId, isLogin: loginType } }).subscribe({
      next: (resp: any) => {
        if (!resp?.length) {
          if (loginType == 'Y') {
            this.showNoApplicableUserMessage();
            this.authService.logoutRedirect();
          }
          return;
        }
        this.handleUserRoutingResponse(resp, azureId, profile_data, loginType);
        this.cdr.markForCheck();
      },
      error: (err: any) => {
        this.userDetailsRequestKey = null;
        console.log('Error fetching user details', err);
      }
    });
  }

  private handleUserRoutingResponse(resp: any, azureId: any, profile_data: any, loginType: string): void {
    if (isRoleSwitchInProgress()) {
      return;
    }
    const currentUserRole = this.roleService.getRoles('profile')?.current_user_role;
    const userProfile = resp?.[0];
    const roleList = this.getRoleList(userProfile);

    this.setRoleStorageOnLogin(roleList, userProfile, loginType);
    localStorage.setItem('firmname', userProfile?.URLFirmName);

    if (this.shouldSkipFispokeAdminRefresh(roleList, loginType)) {
      return;
    }

    const profile = this.buildProfile(userProfile, currentUserRole, azureId, profile_data);
    this.roleService.setRoles('profile', profile);
    if (loginType !== 'Y') {
      this.ensureSessionRoles(roleList, profile);
    }

    if (loginType == 'Y') {
      this.handleLoginRouting(roleList, userProfile, resp, loginType);
      return;
    }
    this.handleExistingSessionRouting(roleList, userProfile, currentUserRole, loginType);
  }

  private getRoleList(userProfile: any): string[] {
    const mappedRoles = userProfile?.UserRoles?.map((item: any) => item.UserRole) ?? [];
    if (mappedRoles.length > 0) {
      return mappedRoles;
    }
    return userProfile?.user_role ? [userProfile.user_role] : [];
  }

  private setRoleStorageOnLogin(roleList: string[], userProfile: any, loginType: string): void {
    if (loginType != 'Y') {
      return;
    }
    if (roleList.length === 1) {
      this.roleService.setRoles('roles', roleList);
      return;
    }
    if (!roleList.length && userProfile?.user_role) {
      this.roleService.setRoles('roles', [userProfile.user_role]);
    }
  }

  /** On refresh, localStorage roles may be cleared; restore so RoleGuard does not throw. */
  private ensureSessionRoles(roleList: string[], profile: any): void {
    const existing = this.roleService.getRoles('roles');
    if (Array.isArray(existing) && existing.length > 0) {
      return;
    }
    const fromProfile = profile?.current_user_role ?? profile?.user_role;
    if (fromProfile) {
      this.roleService.setRoles('roles', [fromProfile]);
      return;
    }
    if (roleList.length === 1) {
      this.roleService.setRoles('roles', roleList);
      return;
    }
    const defaultRole = this.getDefaultRoleForMultipleAssignments(roleList);
    if (defaultRole) {
      this.roleService.setRoles('roles', [defaultRole]);
    }
  }

  private shouldSkipFispokeAdminRefresh(roleList: string[], loginType: string): boolean {
    return roleList.includes('fiSpokeAdmin') &&
      !!this.roleService.getRoles('profile')?.FirmID &&
      loginType == 'N';
  }

  private buildProfile(userProfile: any, currentUserRole: any, azureId: any, profile_data: any): any {
    userProfile['azure_userId'] = azureId;
    userProfile['AdvisorIDs'] = [userProfile?.AdvisorID];
    userProfile['initialAdvisorID'] = userProfile?.AdvisorID;
    userProfile['current_user_role'] = currentUserRole;
    userProfile['advisor_azure_id'] = azureId;
    userProfile['default_azure_id'] = profile_data?.localAccountId;
    userProfile['defaultIsAdvisorDetailsUpdated'] = userProfile?.IsAdvisorDetailsUpdated;
    return userProfile;
  }

  private normalizeRole(role: string): string {
    return (role ?? '').toLowerCase().trim();
  }

  private roleListHas(roleList: string[], role: string): boolean {
    return roleList.some((r) => this.normalizeRole(r) === this.normalizeRole(role));
  }

  private findRoleInList(roleList: string[], role: string): string {
    const target = this.normalizeRole(role);
    return roleList.find((r) => this.normalizeRole(r) === target) ?? role;
  }

  /**
   * Picks the initial role when the user has multiple assignments (no role-selection popup).
   * Admin + Advisor (+ Assistant) → Admin; Advisor + Assistant (no Admin) → Advisor.
   */
  private getDefaultRoleForMultipleAssignments(roleList: string[]): string | null {
    if (!roleList?.length) {
      return null;
    }
    const flags = {
      hasAdmin: this.roleListHas(roleList, 'admin'),
      hasAdvisor: this.roleListHas(roleList, 'advisor'),
      hasAdvisorAssistant: this.roleListHas(roleList, 'advisor assistant'),
    };

    // Priority-ordered rules. The first matching rule wins; this preserves the
    // exact precedence of the original if/else-if chain.
    const rules: Array<{ when: (f: typeof flags) => boolean; role: string }> = [
      { when: (f) => f.hasAdmin && f.hasAdvisor && f.hasAdvisorAssistant, role: 'advisor' },
      { when: (f) => f.hasAdmin && f.hasAdvisor, role: 'advisor' },
      { when: (f) => f.hasAdvisor && f.hasAdvisorAssistant && !f.hasAdmin, role: 'advisor' },
      { when: (f) => f.hasAdmin && f.hasAdvisorAssistant && !f.hasAdvisor, role: 'admin' },
      { when: (f) => f.hasAdmin, role: 'admin' },
      { when: (f) => f.hasAdvisor, role: 'advisor' },
      { when: (f) => f.hasAdvisorAssistant, role: 'advisor assistant' },
    ];

    const matched = rules.find((rule) => rule.when(flags));
    if (matched) {
      return this.findRoleInList(roleList, matched.role);
    }
    return roleList[0] ?? null;
  }

  private applySelectedRoleAndNavigate(
    defaultRole: string,
    userProfile: any,
    resp: any,
    loginType: string
  ): void {
    const role = [defaultRole];
    this.roleService.setRoles('roles', role);
    const profile = this.roleService.getRoles('profile');
    profile['user_role'] = defaultRole;
    profile['current_user_role'] = defaultRole;
    profile['AdvisorIDs'] = [profile?.initialAdvisorID];
    profile['AdvisorID'] = defaultRole === 'advisor' ? profile?.initialAdvisorID : null;
    if (role.includes('advisor')) {
      profile['advisor_azure_id'] = profile?.default_azure_id;
      profile['IsAdvisorDetailsUpdated'] = profile?.defaultIsAdvisorDetailsUpdated;
    }
    this.roleService.setRoles('profile', profile);

    if (role.includes('admin')) {
      this.router.navigate(['/advisor-dashboard/user-management']);
      setTimeout(() => {
        this.CommonService.triggerAdvisorChanged();
      }, 100);
      return;
    }
    if (role.includes('advisor')) {
      this.CommonService.getProductByAdvisorId(profile?.AdvisorID);
      return;
    }
    if (role.includes('advisor assistant')) {
      localStorage.removeItem('assistantAdvisor');
      this.AdvisorDashboardLayoutService.getAssistantAdvisor(profile?.user_id, loginType);
      return;
    }
    if (role.includes('client')) {
      this.handleClientLoginRouting(userProfile, resp);
      return;
    }
    if (role.includes('fiSpokeAdmin')) {
      this.router.navigate(['/advisor-dashboard/client-term-acknowledgment']);
    }
  }

  private handleLoginRouting(roleList: string[], userProfile: any, resp: any, loginType: string): void {
    if (userProfile?.user_role === 'client') {
      localStorage.setItem('popupShown', 'true');
    }

    if (roleList.length > 1) {
      const defaultRole = this.getDefaultRoleForMultipleAssignments(roleList);
      if (defaultRole) {
        this.applySelectedRoleAndNavigate(defaultRole, userProfile, resp, loginType);
      }
      // Legacy: prompt user to pick a role after login
      // this.showChangeRolesDialogeComponent();
      return;
    }
    if (roleList.includes('admin')) {
      this.router.navigate(['/advisor-dashboard/user-management']);
      return;
    }
    if (roleList.includes('advisor')) {
      this.CommonService.getProductByAdvisorId(userProfile?.AdvisorID);
      return;
    }
    if (roleList.includes('client')) {
      this.handleClientLoginRouting(userProfile, resp);
      return;
    }
    if (roleList.includes('fiSpokeAdmin')) {
      this.router.navigate(['/advisor-dashboard/client-term-acknowledgment']);
      return;
    }
    if (roleList.includes('superAdmin')) {
      this.router.navigate(['/advisor-dashboard/user-management']);
      return;
    }
    if (roleList.includes('advisor assistant')) {
      localStorage.removeItem('assistantAdvisor');
      this.AdvisorDashboardLayoutService.getAssistantAdvisor(userProfile?.user_id, loginType);
      return;
    }

    this.showNoApplicableUserMessage();
    this.authService.logoutRedirect();
  }

  private handleClientLoginRouting(userProfile: any, resp: any): void {
    this.CommonService.setClientAgreementGateActive(true);
    this.navigateToClientAgreementGate();
    this.CommonService.getProductByAdvisorIdClient(userProfile?.AdvisorID);
    this.ClientTermsAcknowledgmentService.getClientBankAgreementChangeHysaApplications({ body: { ClientID: userProfile?.ClientID } }).subscribe({
      next: (response: any) => {
        if (response?.length > 0) {
          this.clientChangeAgreementsList = response;
          this.clientChangeAgreementsDetails = response?.[0];
          this.showCustomerWarning = true;
          localStorage.setItem('clientChangeAgreementShow', 'true');
          localStorage.setItem('clientChangeAgreementsList', JSON.stringify(this.clientChangeAgreementsList));
          this.navigateToClientAgreementGate();
          this.cdr.markForCheck();
          return;
        }
        this.completeClientLoginWithoutAgreementWarning(resp);
      },
      error: (_err: any) => {
        this.completeClientLoginWithoutAgreementWarning(resp);
      }
    });
  }

  private navigateToClientAgreementGate(): void {
    this.syncClientAgreementWarningFromStorage();
    const url = this.router.url.split('?')[0].replace(/\/$/, '');
    if (url === '/client-agreement-gate') {
      return;
    }
    this.router.navigateByUrl('/client-agreement-gate', { replaceUrl: true });
  }

  private completeClientLoginWithoutAgreementWarning(resp: any): void {
    this.CommonService.setClientAgreementGateActive(false);
    localStorage.removeItem('clientChangeAgreementShow');
    this.router.navigate(['/advisor-dashboard/dashboard-customer']);
    this.getResubmissionDetails(resp);
    this.cdr.markForCheck();
  }

  private handleExistingSessionRouting(roleList: string[], userProfile: any, currentUserRole: any, loginType: string): void {
    if (roleList.length > 1 && currentUserRole == undefined) {
      const defaultRole = this.getDefaultRoleForMultipleAssignments(roleList);
      if (defaultRole) {
        this.applySelectedRoleAndNavigate(defaultRole, userProfile, [userProfile], loginType);
      } else {
        this.roleService.setRoles('roles', []);
      }
      // Legacy: prompt user to pick a role when session has no active role
      // this.showChangeRolesDialogeComponent();
      return;
    }

    if (roleList.includes('advisor assistant')) {
      if (currentUserRole != null && currentUserRole === 'advisor assistant') {
        this.AdvisorDashboardLayoutService.getAssistantAdvisor(userProfile?.user_id, loginType);
      }
      return;
    }

    if (userProfile?.user_role == 'client') {
      this.restoreClientAgreementWarning();
      return;
    }

    if (loginType === 'N') {
      this.CommonService.triggerAdvisorChanged();
    }
  }

  private restoreClientAgreementWarning(): void {
    if (!this.syncClientAgreementWarningFromStorage()) {
      return;
    }
    this.navigateToClientAgreementGate();
    this.cdr.markForCheck();
  }

  /** @returns true when pending agreement data was restored from storage */
  private syncClientAgreementWarningFromStorage(): boolean {
    const list = localStorage.getItem('clientChangeAgreementsList');
    const isShown = localStorage.getItem('clientChangeAgreementShow');
    const parsedList = list ? JSON.parse(list) : [];
    if (parsedList?.length > 0 && isShown === 'true') {
      this.clientChangeAgreementsList = parsedList;
      this.clientChangeAgreementsDetails = parsedList?.[0];
      this.showCustomerWarning = true;
      this.CommonService.setClientAgreementGateActive(true);
      return true;
    }
    return false;
  }

  private showNoApplicableUserMessage(): void {
    this.MessageService.add({
      severity: 'error',
      summary: 'Error',
      detail: 'We couldn’t verify your account. Please sign out and sign in again. If this continues, contact your administrator',
      life: 5000
    });
  }

  private showMobileNotSupportedMessage(): void {
    this.MessageService.add({
      severity: 'error',
      summary: 'Unsupported Device',
      detail: 'This application is not supported on mobile devices. Please sign in from a desktop browser.',
      life: 5000
    });
  }
  getResubmissionDetails(data: any) {
    const ClientID = data?.[0]?.ClientID;
    this.ReSubmissionControllerService.getClientHysaApplicationResubmitReviewChange({ body: { ClientID: ClientID } }).subscribe({
      next: (resp: any) => {
        if(resp?.v_status === 'FAILED') {
          this.router.navigate(['/advisor-dashboard/dashboard-customer']);
        } else {
          this.resubmitApplications = resp;
          this.clientName = `${data?.[0]?.first_name} ${data?.[0]?.last_name}`;
          this.showResubmitApplications = true;
          this.cdr.markForCheck();
        }
      }
    })
  }
  closeResubmitApplications() {
    this.showResubmitApplications = false;
    this.router.navigate(['/advisor-dashboard/dashboard-customer']);
  }
  navigateToDetails(account: any): void {
    this.showResubmitApplications = false;
    const state = {
      AD: account?.ApplicationID,
      AHD: account?.ApplicationHolderID,
      AT: account?.HolderType,
      CD: account?.ClientID
    };

    // Navigate programmatically to the desired route with router state
    this.router.navigate(['/advisor-dashboard/create-new-account-customer-new'], { state });
  }
  checkAndSetActiveAccount() {
    /**
     * If no active account set but there are accounts signed in, sets first account to active account
     * To use active account set here, subscribe to inProgress$ first in your component
     * Note: Basic usage demonstrated. Your app may require more complicated account selection logic
     */
    let activeAccount = this.authService.instance.getActiveAccount();

    if (
      !activeAccount &&
      this.authService.instance.getAllAccounts().length > 0
    ) {
      let accounts = this.authService.instance.getAllAccounts();
      // add your code for handling multiple accounts here
      this.ensureActiveAccount(accounts[0]);
    }
  }

  goToBankDocument() {
    this.showCustomerWarning = false;
    this.CommonService.setClientAgreementGateActive(false);
    this.router.navigate(['/advisor-dashboard/bank-agreements']);
    this.cdr.markForCheck();
  }

  // unsubscribe to events when component is destroyed
  ngOnDestroy(): void {
    this._destroying$.next(undefined);
    this._destroying$.complete();
    localStorage.removeItem('clientChangeAgreementsList')
  }

  showChangeRolesDialogeComponent() {
    this.showChangeRolesDialoge = this.dialogService.open(ChangeRolesDialogComponent, {
      showHeader: false,
      maximizable: false,
      draggable: false,
      resizable: true,
      closeOnEscape: true,
      width: '35vw',
      data: {
        isShowCloseButton: false,
      },
      styleClass: 'mx-4',
      contentStyle: {
        padding: 0,
        marginLeft: 10,
      },
    });
  }
}
